import React, { useEffect, useState } from "react";
import Modal from "@mui/material/Modal";
import Fade from "@mui/material/Fade";
import Backdrop from "@mui/material/Backdrop";
import { Box, IconButton, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import Grid from "@mui/material/Grid2";
import toast from "react-hot-toast";
import { resolveProductIntakeApi } from "../../../utils/productIntakeApi";
import DeleteImageConfirmation from "./DeleteImageConfirmation";
import {
  StyledBoxImage,
  StyledBoxReview,
  StyledGrid,
  StyedImg,
} from "../review-approve/style";

const IMAGE_ROW_HEIGHT = 210;
const GRID_ROW_GAP = 16;
const VISIBLE_ROWS = 2;
const SCROLL_AREA_PADDING = 32;
const MODAL_WIDTH = 900;

const scrollAreaMaxHeight =
  VISIBLE_ROWS * IMAGE_ROW_HEIGHT + (VISIBLE_ROWS - 1) * GRID_ROW_GAP + SCROLL_AREA_PADDING;

const filterValidImages = (images) =>
  images.filter((url) => typeof url === "string" && url.trim());

export default function ProductImageGalleryModal({
  open,
  handleClose,
  images = [],
  productName = "",
  productId,
  invoiceImage = null,
  recordList,
  intakeScope = "admin",
  readOnly = false,
}) {
  const intakeApi = resolveProductIntakeApi(intakeScope);
  const [localImages, setLocalImages] = useState([]);
  const [previewIndex, setPreviewIndex] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [imageToDelete, setImageToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [invoicePreview, setInvoicePreview] = useState(false);

  useEffect(() => {
    if (open) {
      setLocalImages(filterValidImages(images));
      setPreviewIndex(null);
    }
  }, [open, images]);

  const closePreview = () => setPreviewIndex(null);

  const handleModalClose = () => {
    if (isDeleting) return;
    closePreview();
    handleClose();
  };

  const handleDeleteClick = (event, url) => {
    event.stopPropagation();
    setImageToDelete(url);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!productId || !imageToDelete) {
      toast.error("Image details are missing.");
      return;
    }

    try {
      setIsDeleting(true);
      const response = await intakeApi.deleteImage({
        id: productId,
        imageUrl: imageToDelete,
      });

      const updatedImages = localImages.filter((url) => url !== imageToDelete);
      setLocalImages(updatedImages);
      setPreviewIndex(null);
      recordList?.();
      toast.success(response?.data?.message || "Image deleted successfully");
    } catch (error) {
      toast.error(error?.data?.message || "Failed to delete image");
    } finally {
      setIsDeleting(false);
      setImageToDelete(null);
      setDeleteConfirmOpen(false);
    }
  };

  return (
    <>
      <Modal open={open} onClose={handleModalClose}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: MODAL_WIDTH,
            maxWidth: "calc(100vw - 32px)",
            maxHeight: "90vh",
            background: "#fff",
            borderRadius: "12px",
            outline: "none",
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
            boxShadow: "0 24px 60px rgba(11, 35, 115, 0.18)",
          }}
        >
          <Box
            sx={{
              background: "#126ABC",
              color: "#fff",
              px: 3,
              py: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexShrink: 0,
            }}
          >
            <Typography sx={{ fontSize: 18, fontWeight: 600 }}>
              {productName ? `Product Images — ${productName}` : "Product Images"}
            </Typography>
            <IconButton
              onClick={handleModalClose}
              sx={{ color: "#fff" }}
              size="small"
              disabled={isDeleting}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <StyledBoxReview
            sx={{
              flex: "1 1 auto",
              minHeight: 0,
              maxHeight: scrollAreaMaxHeight,
              overflowY: "auto",
            }}
          >
            {localImages.length === 0 ? (
              <Typography sx={{ textAlign: "center", color: "text.secondary", py: 4 }}>
                No images uploaded for this product.
              </Typography>
            ) : (
              <Grid container columnSpacing={2} rowSpacing={2}>
                {localImages.map((url, index) => (
                  <StyledGrid
                    key={`${url}-${index}`}
                    size={{ lg: 4, md: 4, sm: 6, xs: 6 }}
                  >
                    <Box
                      sx={{
                        position: "relative",
                        ...(!readOnly && {
                          "&:hover .delete-image-btn": {
                            opacity: isDeleting ? 0 : 1,
                            transform: isDeleting ? "translateY(-6px)" : "translateY(0)",
                          },
                        }),
                      }}
                    >
                      {!readOnly && (
                      <IconButton
                        className="delete-image-btn"
                        onClick={(event) => handleDeleteClick(event, url)}
                        sx={{
                          position: "absolute",
                          top: 8,
                          right: 8,
                          width: 36,
                          height: 36,
                          background: "rgba(255, 255, 255, 0.96)",
                          color: "#D32F2F",
                          opacity: 0,
                          transform: "translateY(-6px)",
                          transition: "all 0.2s ease",
                          zIndex: 2,
                          "&:hover": { background: "#fff" },
                        }}
                        disabled={isDeleting}
                        size="small"
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                      )}
                      <StyledBoxImage
                        onClick={() => setPreviewIndex(index)}
                        sx={{ height: IMAGE_ROW_HEIGHT }}
                      >
                        <StyedImg src={url} alt={`product-${index + 1}`} loading="lazy" />
                      </StyledBoxImage>
                    </Box>
                  </StyledGrid>
                ))}
              </Grid>
            )}
          </StyledBoxReview>

          {invoiceImage && (
            <Box sx={{ px: 3, py: 2, borderTop: "1px solid #e0e0e0", flexShrink: 0 }}>
              <Typography sx={{ fontSize: 14, fontWeight: 600, mb: 1.5, color: "#126ABC" }}>
                Invoice / Payment Proof
              </Typography>
              <Box
                sx={{
                  position: "relative",
                  display: "inline-block",
                  cursor: "pointer",
                  borderRadius: 2,
                  overflow: "hidden",
                  border: "1px solid #e0e0e0",
                }}
                onClick={() => setInvoicePreview(true)}
              >
                <Box
                  component="img"
                  src={invoiceImage}
                  alt="invoice"
                  sx={{ height: 120, width: "auto", display: "block", objectFit: "contain" }}
                />
              </Box>
            </Box>
          )}
        </Box>
      </Modal>

      <Modal
        open={previewIndex !== null}
        onClose={closePreview}
        slots={{ backdrop: Backdrop }}
        slotProps={{ backdrop: { timeout: 300, style: { background: "rgba(10,20,50,0.9)" } } }}
      >
        <Fade in={previewIndex !== null} timeout={250}>
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              outline: "none",
            }}
          >
            <Box
              component="img"
              src={previewIndex !== null ? localImages[previewIndex] : ""}
              alt="preview"
              sx={{
                maxWidth: "88vw",
                maxHeight: "82vh",
                borderRadius: "12px",
                boxShadow: "0 8px 40px rgba(0,0,0,0.6)",
                display: "block",
                objectFit: "contain",
              }}
            />
          </Box>
        </Fade>
      </Modal>

      {invoiceImage && (
        <Modal
          open={invoicePreview}
          onClose={() => setInvoicePreview(false)}
          slots={{ backdrop: Backdrop }}
          slotProps={{ backdrop: { timeout: 300, style: { background: "rgba(10,20,50,0.9)" } } }}
        >
          <Fade in={invoicePreview} timeout={250}>
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                outline: "none",
              }}
            >
              <Box
                component="img"
                src={invoiceImage}
                alt="invoice-preview"
                sx={{
                  maxWidth: "88vw",
                  maxHeight: "82vh",
                  borderRadius: "12px",
                  boxShadow: "0 8px 40px rgba(0,0,0,0.6)",
                  display: "block",
                  objectFit: "contain",
                }}
              />
            </Box>
          </Fade>
        </Modal>
      )}

      <DeleteImageConfirmation
        open={deleteConfirmOpen}
        onClose={() => !isDeleting && setDeleteConfirmOpen(false)}
        onConfirm={handleConfirmDelete}
        isDeleting={isDeleting}
      />
    </>
  );
}
